import { checkboxAnatomy } from '@chakra-ui/anatomy';
import {
  createMultiStyleConfigHelpers,
  cssVar,
} from '@chakra-ui/styled-system';

const { defineMultiStyleConfig, definePartsStyle } =
  createMultiStyleConfigHelpers(checkboxAnatomy.keys);

const $controlBg = cssVar('checkbox-control-bg');
const $controlBgChecked = cssVar('checkbox-control-bg-checked');
const $controlBgCheckedHover = cssVar('checkbox-control-bg-checked-hover');
const $controlBorderColor = cssVar('checkbox-control-border-color');
const $controlBorderColorChecked = cssVar(
  'checkbox-control-border-color-checked'
);
const $controlBorderColorCheckedHover = cssVar(
  'checkbox-control-border-color-checked-hover'
);
const $controlBorderColorHover = cssVar('checkbox-control-border-color-hover');
const $iconColor = cssVar('checkbox-icon-color');
const $labelColor = cssVar('checkbox-label-color');
const $labelColorHover = cssVar('checkbox-label-color-hover');

const baseStyle = definePartsStyle(() => ({
  container: {
    // Container for the checkbox and label
    // Initialize hover variables with default border/label colors
    [$controlBorderColorHover.variable]: 'colors.border.strong',
    [$labelColorHover.variable]: 'colors.fonts.primary',
    // Initialize checked hover variables with default checked colors
    [$controlBgCheckedHover.variable]: 'colors.buttons.primary.bg',
    [$controlBorderColorCheckedHover.variable]: 'colors.buttons.primary.bg',
    _disabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    _hover: {
      // When container is hovered, update CSS variables for control and label
      [$controlBorderColorHover.variable]: 'colors.buttons.primary.bg-hover',
      [$labelColorHover.variable]: 'colors.buttons.primary.bg-hover',
      // When container is hovered and checkbox is checked, use hover colors
      [$controlBgCheckedHover.variable]: 'colors.buttons.primary.bg-hover',
      [$controlBorderColorCheckedHover.variable]:
        'colors.buttons.primary.bg-hover',
    },
    _dark: {
      [$controlBorderColorHover.variable]: 'colors.border.strong',
      [$labelColorHover.variable]: 'colors.fonts.primary',
      [$controlBgCheckedHover.variable]: 'colors.buttons.primary.bg',
      [$controlBorderColorCheckedHover.variable]: 'colors.buttons.primary.bg',
      _hover: {
        [$controlBorderColorHover.variable]: 'colors.buttons.primary.bg-hover',
        [$labelColorHover.variable]: 'colors.buttons.primary.bg-hover',
        [$controlBgCheckedHover.variable]: 'colors.buttons.primary.bg-hover',
        [$controlBorderColorCheckedHover.variable]:
          'colors.buttons.primary.bg-hover',
      },
    },
  },
  control: {
    bg: $controlBg.reference,
    border: '1px solid',
    // Use hover color variable (set by container on hover, defaults to border.strong)
    borderColor: $controlBorderColorHover.reference,
    borderRadius: '4px',
    _checked: {
      // Use checked hover variables (set by container on hover, defaults to buttons.primary.bg)
      bg: $controlBgCheckedHover.reference,
      borderColor: $controlBorderColorCheckedHover.reference,
      _hover: {
        bg: $controlBgCheckedHover.reference,
        borderColor: $controlBorderColorCheckedHover.reference,
      },
    },
    _focus: {
      boxShadow: 'none',
      outline: 'none',
    },
    _focusVisible: {
      boxShadow: 'none',
      outline: '2px solid',
      outlineColor: 'accent.500',
      outlineOffset: '2px',
    },
    _disabled: {
      bg: $controlBg.reference,
      borderColor: $controlBorderColor.reference,
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    _dark: {
      [$controlBg.variable]: 'transparent',
      [$controlBgChecked.variable]: 'colors.buttons.primary.bg',
      [$controlBorderColor.variable]: 'colors.border.strong',
      [$controlBorderColorChecked.variable]: 'colors.buttons.primary.bg',
    },
  },
  icon: {
    color: $iconColor.reference,
    _dark: {
      [$iconColor.variable]: 'colors.white',
    },
  },
  label: {
    fontSize: '16px',
    lineHeight: '24px',
    color: $labelColorHover.reference,
    _dark: {
      [$labelColor.variable]: 'colors.fonts.primary',
    },
  },
}));

export const checkboxTheme = defineMultiStyleConfig({
  baseStyle,
  defaultProps: {
    colorScheme: 'accent',
  },
});
